import React, { useState, useMemo, useCallback, useEffect } from "react";
import { Box, Divider, InputAdornment, Tabs, Tab, Stack } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import {
  GridColDef,
  GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
} from "@mui/x-data-grid-premium";
import { format, eachDayOfInterval } from "date-fns";
import ByzMuiTypography from "src/components/core-components/mui/inputs/ByzMuiTypography";
import ByzMuiTextField from "src/components/core-components/mui/inputs/ByzMuiTextField";
import { ByzMuiDatePicker } from "src/components/core-components/mui/inputs/ByzMuiDatePicker";
import ByzMuiSelect from "src/components/core-components/mui/ByzMuiSelect";
import { ByzMuiCheckBox } from "src/components/core-components/mui/ByzMuiCheckBox";
import ByzMuiButton from "src/components/core-components/mui/buttons/ByzMuiButton";
import ByzMuiCloseButton from "src/components/core-components/mui/buttons/ByzMuiCloseButton";
import ByzToggleIconButton from "src/components/core-components/mui/buttons/ByzToggleIconButton.tsx";
import ByzMuiDataGrid from "src/components/core-components/mui/data-grid";
import ByzGrdEditButton from "src/components/core-components/mui/buttons-grid/ByzGrdEditButton";
import PageMorning from "../morning";
import PageAfternoon from "../afternoon";
import PageEvening from "../evening";
import ByzMuiAddButton from "src/components/core-components/mui/buttons/ByzMuiAddButton";
import { useTranslate } from "src/locales";
import OpenComponentDialog from "src/components/shared-components/modal/openComponentModal";
import EditPage from "./edit-page";
import { latestDietRows } from "../latest-diet/data";
import PageHistory from "../../page-history";
import PageFavorites from "../../page-favorites";     //CKAPISIZ;452424;05052004

//IDOGAN;449733;25042025
const tabSx = (active: number, idx: number) => ({
  width: "100%",
  textTransform: "none",
  justifyContent: "flex-start",
  borderBottom: "1px solid #E0E0E0",
  bgcolor: active === idx ? "#F4F7FE" : "transparent",
  pl: 0,
});

interface DietPopupProps {
  onClose: () => void;
  onExportGrid: (cfg: {
    parentRows: any[];
    parentColumns: GridColDef[];
    childMap: Record<string, any[]>;
    childColumns: GridColDef[];
  }) => void;
}

const DietPopup: React.FC<DietPopupProps> = ({ onClose, onExportGrid }) => {
  const { t } = useTranslate();

  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [searchValue, setSearchValue] = useState("");
  const [refakatci, setRefakatci] = useState<string | null>(null);
  const [araOgun, setAraOgun] = useState(false);
  const [isEyeOpen, setIsEyeOpen] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isArrowUp, setIsArrowUp] = useState(false);
  const [morningRows, setMorningRows] = useState<any[]>([]);
  const [afternoonRows, setAfternoonRows] = useState<any[]>([]);
  const [eveningRows, setEveningRows] = useState<any[]>([]);
  const [selectionState, setSelectionState] = useState<
    Record<string, string[]>
  >({});
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [latestRows, setLatestRows] = useState<any[]>([]);
  const [expandedRowIds, setExpandedRowIds] = useState<string[]>([]);
  useEffect(() => { ////IDOGAN;452471;05052025;OTOMATIK OLARAK CHILD SECIMLERINI VE DETAY PANELINI ACAR
    if (latestRows.length === 0 || !startDate || !endDate) {
      return;
    }
    const dates = eachDayOfInterval({ start: startDate, end: endDate });
    const sel: Record<string, string[]> = {};
    dates.forEach((d) => {
      const key = format(d, "dd.MM.yyyy");
      sel[`p_${key}`] = latestDietRows.map((r) => `c_${key}_${r.id}`);
    });
    setSelectionState(sel);

    //IDOGAN;452471;05052025;DETAY PANELLERI OTOMATIK OLARAK ACILIR(SADECE SON DIYET ICIN)
    setExpandedRowIds(dates.map((d) => `p_${format(d, "dd.MM.yyyy")}`));
  }, [latestRows, startDate, endDate]);
  const selectionModel = useMemo(
    () => Object.values(selectionState).flat(),
    [selectionState]
  );
  const [editRow, setEditRow] = useState<any>(null);
  const parentRows = useMemo(() => {
    if (!startDate || !endDate) return [];
    if (startDate > endDate) return [];
    return eachDayOfInterval({ start: startDate, end: endDate }).map((d) => {
      const dateKey = format(d, "dd.MM.yyyy");
      return {
        //HER PARENT ID TARIHE BAGLI
        id: `p_${dateKey}`,
        diyetZamani: dateKey,
        onayDurumu: "Beklemede",
      };
    });
  }, [startDate, endDate]);

  const childMap = useMemo(() => {
    if (!startDate || !endDate) return {};
    if (startDate > endDate) return {};
    const items = [
      ...morningRows,
      ...afternoonRows,
      ...eveningRows,
      ...latestRows, ////IDOGAN;452471;05052025;SON DIYET VERILERI
    ];
    return eachDayOfInterval({ start: startDate, end: endDate }).reduce<
      Record<string, any[]>
    >((acc, d) => {
      const dateKey = format(d, "dd.MM.yyyy");
      acc[dateKey] = items.map((r) => ({
        id: `c_${dateKey}_${r.id}`,
        diyetZamani: dateKey,
        onayDurumu: "Beklemede",
        ...r,
        refakatci: "Refakatçi 1",
        islemZamani: dateKey,
        islemYapan: "BYZ",
        onayIptal: "Beklemede",
      }));
      return acc;
    }, {});
  }, [startDate, endDate, morningRows, afternoonRows, eveningRows, latestRows]);
  useEffect(() => {
    if (!startDate || !endDate || latestRows.length === 0) return;

    const dates = eachDayOfInterval({ start: startDate, end: endDate });
    const nextSelection: Record<string, string[]> = {};

    dates.forEach((d) => {
      const key = format(d, "dd.MM.yyyy");
      const pId = `p_${key}`;

      nextSelection[pId] = (childMap[key] || []).map((child) => child.id);
    });

    setSelectionState(nextSelection);

    setExpandedRowIds(dates.map((d) => `p_${format(d, "dd.MM.yyyy")}`));
  }, [latestRows, childMap, startDate, endDate]);

  const handleExportSelected = () => {
    ////IDOGAN;452471;05052025;HANGI TARIHLERDE SECIM OLDUGUNU BELIRLE
    const filteredParents = parentRows.filter((r) => !!selectionState[r.id]);

    ////IDOGAN;452471;05052025;O TARIHE AIT SECILI CHILD SATIRALRINI CEK
    const filteredChildMap: Record<string, any[]> = {};
    for (const [pid, cids] of Object.entries(selectionState)) {
      const date = pid.replace(/^p_/, "");
      filteredChildMap[date] = (childMap[date] || []).filter((r) =>
        cids.includes(r.id)
      );
    }

    onExportGrid({
      parentRows: filteredParents,
      parentColumns,
      childMap: filteredChildMap,
      childColumns,
    });
    onClose();
  };

  const detailCols: GridColDef[] = [
    {
      field: "adi",
      headerName: t("patient-diet-procedures.add-popup.name"),
      flex: 1,
    },
    {
      field: "ogun",
      headerName: t("patient-diet-procedures.add-popup.meal"),
      flex: 1,
    },
    {
      field: "diyetTipi",
      headerName: t("patient-diet-procedures.add-popup.diet-type"),
      flex: 1,
    },
    {
      field: "not",
      headerName: t("patient-diet-procedures.add-popup.note"),
      flex: 1,
    },
    {
      field: "refakatci",
      headerName: t("patient-diet-procedures.add-popup.companion"),
      flex: 1,
    },
    {
      field: "islemZamani",
      headerName: t("patient-diet-procedures.add-popup.processtime"),
      flex: 1,
    },
    {
      field: "islemYapan",
      headerName: t("patient-diet-procedures.add-popup.processor"),
      flex: 1,
    },
    {
      field: "onayIptal",
      headerName: t("patient-diet-procedures.add-popup.approve-or-cancel"),
      flex: 1,
    },
    {
      field: "duzenle",
      headerName: t("patient-diet-procedures.add-popup.edit"),
      flex: 1,
      renderCell: (params) => (
        <ByzGrdEditButton
          onClick={() => {
            setEditRow(params.row);
            setEditDialogOpen(true);
          }}
        />
      ),
    },
  ];

  const [parentColumns, setParentColumns] = useState<GridColDef[]>([
    {
      field: "diyetZamani",
      headerName: t("patient-diet-procedures.add-popup.diet-time"),
      flex: 1,
    },
    {
      field: "onayDurumu",
      headerName: t("patient-diet-procedures.add-popup.approval-status"),
      flex: 1,
      cellClassName: (params) =>
        params.value === "Beklemede" ? "status-beklemede" : "status-gonderildi",
    },
    ...detailCols.map((col) =>
      col.field === "duzenle"
        ? {
          ...col,
          renderCell: () => null,
          sortable: false,
        }
        : col
    ),
  ]);

  const [childColumns, setChildColumns] = useState<GridColDef[]>([
    {
      field: "diyetZamani",
      headerName: t("patient-diet-procedures.add-popup.diet-time"),
      flex: 1,
    },
    {
      field: "onayDurumu",
      headerName: t("patient-diet-procedures.add-popup.approval-status"),
      flex: 1,
      cellClassName: (params) =>
        params.value === "Beklemede" ? "status-beklemede" : "status-gonderildi",
    },
    ...detailCols,
  ]);

  const handleColumnResize = useCallback(
    ({ colDef, width }: { colDef: GridColDef; width: number }) => {
      const { field } = colDef;
      setParentColumns((cols) =>
        cols.map((c) =>
          c.field === field ? { ...c, width, flex: undefined } : c
        )
      );
      setChildColumns((cols) =>
        cols.map((c) =>
          c.field === field ? { ...c, width, flex: undefined } : c
        )
      );
    },
    []
  );

  const parentActionsColumn = useMemo<GridColDef>(() => {
    const parentActions: GridColDef = {
      field: "__select_parent__",
      headerName: "",
      width: 52,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderHeader: () => { ////IDOGAN;452471;05052025;TOPLAM CHILD SAYISINI AL
        const totalChildren = parentRows.reduce(
          (sum, r) => sum + (childMap[r.diyetZamani]?.length || 0),
          0
        );
        const selectedCount = Object.values(selectionState).flat().length;
        const allSel = totalChildren > 0 && selectedCount === totalChildren;
        const anySel = selectedCount > 0 && !allSel;

        return (
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <ByzMuiCheckBox
              size="small"
              checked={allSel}
              indeterminate={anySel}
              onChange={() => {
                if (allSel) {
                  setSelectionState({});
                } else {
                  const next: Record<string, string[]> = {};
                  parentRows.forEach((r) => {
                    const key = r.id;
                    const kids = childMap[r.diyetZamani] || [];
                    next[key] = kids.map((c) => c.id);
                  });
                  setSelectionState(next);
                }
              }}
            />
          </Box>
        );
      },
      renderCell: ({ row }: any) => {
        const pid = row.id as string;
        const children = childMap[row.diyetZamani] || [];
        const selectedChildren = selectionState[pid] || [];
        const allSel =
          children.length > 0 && selectedChildren.length === children.length;
        const anySel = selectedChildren.length > 0 && !allSel;

        return (
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <ByzMuiCheckBox
              size="small"
              checked={allSel}
              indeterminate={anySel}
              onChange={() =>
                setSelectionState((prev) => {
                  const next = { ...prev };
                  if (allSel) {
                    delete next[pid];
                  } else {
                    next[pid] = children.map((c) => c.id);
                  }
                  return next;
                })
              }
            />
          </Box>
        );
      },
    };
    return parentActions;
  }, [childMap, selectionState, parentRows]);

  const childActionsColumn = useMemo<GridColDef>(
    () => ({
      field: "__select_child__",
      headerName: "",
      width: 52,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderHeader: () => null,
      renderCell: ({ row }: any) => {
        const dateKey = row.diyetZamani as string;
        const pid = `p_${dateKey}`;
        const cid = row.id as string;
        const selectedChildren = selectionState[pid] || [];

        const checked = selectedChildren.includes(cid);

        return (
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <ByzMuiCheckBox
              size="small"
              checked={checked}
              onChange={() =>
                setSelectionState((prev) => {
                  const next = { ...prev };
                  const setForParent = new Set(next[pid] || []);
                  if (setForParent.has(cid)) setForParent.delete(cid);
                  else setForParent.add(cid);
                  if (setForParent.size) next[pid] = Array.from(setForParent);
                  else delete next[pid];
                  return next;
                })
              }
            />
          </Box>
        );
      },
    }),
    [selectionState]
  );

  const indentColumn = useMemo<GridColDef>(
    () => ({
      field: "__indent__",
      headerName: "",
      width: 52,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderHeader: () => null,
      renderCell: (params) => {
        const rowIds = Array.from(params.api.getRowModels().keys());
        if (rowIds[0] !== params.id) {
          return null;
        }
        return (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box component="img" src="/assets/icons/vector/line.svg" />
          </Box>
        );
      },
    }),
    []
  );
  return (
    <Box sx={{ display: "flex", height: "100%" }}>
      <Box
        sx={{ width: 280, borderRight: "1px solid #E0E0E0", overflowY: "auto" }}
      >
        <Box sx={{ p: 2 }}>
          <ByzMuiTypography isTitle>
            {t("patient-diet-procedures.add-popup.diet-operations")}
          </ByzMuiTypography>
          <ByzMuiTypography sx={{ color: "gray", mt: 1 }}>
            {t("patient-diet-procedures.add-popup.fullname")}
          </ByzMuiTypography>
          <ByzMuiTypography>Mertcan İşkar</ByzMuiTypography>
          <ByzMuiTypography sx={{ color: "gray", mt: 2 }}>
            {t("patient-diet-procedures.add-popup.file-prot")}
          </ByzMuiTypography>
          <ByzMuiTypography>1337 / 852456</ByzMuiTypography>
        </Box>
        <Box sx={{ px: 2, pb: 2 }}>
          <ByzMuiTextField
            fullWidth
            size="small"
            placeholder="Ara"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Divider />
        <Tabs
          orientation="vertical"
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          TabIndicatorProps={{ style: { display: "none" } }}
          sx={{ ".MuiTabs-flexContainer": { alignItems: "stretch" } }}
        >
          {[
            t("patient-diet-procedures.add-popup.history"),
            t("patient-diet-procedures.add-popup.favorites"),
            t("patient-diet-procedures.add-popup.all"),
            t("patient-diet-procedures.add-popup.morning"),
            t("patient-diet-procedures.add-popup.snack-morning"),
            t("patient-diet-procedures.add-popup.noon"),
            t("patient-diet-procedures.add-popup.snack-noon"),
            t("patient-diet-procedures.add-popup.evening"),
            t("patient-diet-procedures.add-popup.snack-evening"),
          ].map((label, idx) => (
            <Tab
              key={idx}
              label={label}
              value={idx}
              icon={<ChevronRightIcon />}
              iconPosition="start"
              sx={tabSx(activeTab, idx)}
            />
          ))}
        </Tabs>
      </Box>

      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflowY: "auto",
        }}
      >
        <Box sx={{ p: 2, display: "flex", justifyContent: "space-between" }}>
          <ByzMuiTypography isTitle>
            {t("patient-diet-procedures.add-popup.diet-operations")}
          </ByzMuiTypography>
          <ByzMuiCloseButton onClick={onClose} />
        </Box>

        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          sx={{ px: 2, mb: 2 }}
        >
          <ByzMuiDatePicker
            label={t("patient-diet-procedures.add-popup.start-date")}
            value={startDate}
            onChange={setStartDate}
          />
          <ByzMuiDatePicker
            label={t("patient-diet-procedures.add-popup.end-date")}
            value={endDate}
            onChange={setEndDate}
          />
          <ByzMuiSelect
            options={[]}
            label={t("patient-diet-procedures.add-popup.companion")}
            placeholder={t("patient-diet-procedures.add-popup.companion")}
            multiple={false}
            sx={{ width: 250 }}
            value={refakatci}
            renderInput={(params) => <ByzMuiTextField {...params} />}
            onChange={(_, v) => setRefakatci(v as string | null)}
          />
          <Box
            sx={{ ml: "auto", display: "flex", alignItems: "center", mr: 2 }}
          >
            <ByzMuiCheckBox
              checked={araOgun}
              onChange={(e) => setAraOgun(e.target.checked)}
            />
            <ByzMuiTypography marginLeft="0px">
              {t("patient-diet-procedures.add-popup.add-snack")}
            </ByzMuiTypography>
          </Box>
          <ByzMuiButton
            onClick={() => {
              //IDOGAN;452471;05052025;SON DIYET SATIRLARINI EKLE
              setLatestRows(latestDietRows);
            }}
            icon={<AddIcon />}
            sx={{
              backgroundColor: "#63B4FF33",
              color: "#2979C3",
            }}
            marginLeft="0px"
          >
            {t("patient-diet-procedures.add-popup.add-latest-diet")} (
            {endDate ? format(endDate, "dd.MM.yyyy") : "--"})
          </ByzMuiButton>
        </Stack>

        {!isFullscreen && activeTab === 0 && (//MUZUN;452440;02052025;
          <PageHistory
            startDate={startDate}
            endDate={endDate}
            selectedIds={morningRows.map((r) => r.id)}
            onToggleRow={(row) =>
              setMorningRows((prev) =>
                prev.some((x) => x.id === row.id)
                  ? prev.filter((x) => x.id !== row.id)
                  : [...prev, row]
              )
            }
          />
        )}
        {!isFullscreen && activeTab === 1 && (  //CKAPISIZ;452424;05052004
          <PageFavorites
            startDate={startDate}
            endDate={endDate}
            selectedIds={morningRows.map((r) => r.id)}
            onToggleRow={(row) =>
              setMorningRows((prev) =>
                prev.some((x) => x.id === row.id)
                  ? prev.filter((x) => x.id !== row.id)
                  : [...prev, row]
              )
            }
          />
        )}
        {!isFullscreen && activeTab === 3 && (
          <PageMorning
            startDate={startDate}
            endDate={endDate}
            selectedIds={morningRows.map((r) => r.id)}
            onToggleRow={(row) =>
              setMorningRows((prev) =>
                prev.some((x) => x.id === row.id)
                  ? prev.filter((x) => x.id !== row.id)
                  : [...prev, row]
              )
            }
          />
        )}
        {!isFullscreen && activeTab === 5 && (
          <PageAfternoon
            startDate={startDate}
            endDate={endDate}
            selectedIds={afternoonRows.map((r) => r.id)}
            onToggleRow={(row) =>
              setAfternoonRows((prev) =>
                prev.some((x) => x.id === row.id)
                  ? prev.filter((x) => x.id !== row.id)
                  : [...prev, row]
              )
            }
          />
        )}
        {!isFullscreen && activeTab === 7 && (
          <PageEvening
            startDate={startDate}
            endDate={endDate}
            selectedIds={eveningRows.map((r) => r.id)}
            onToggleRow={(row) =>
              setEveningRows((prev) =>
                prev.some((x) => x.id === row.id)
                  ? prev.filter((x) => x.id !== row.id)
                  : [...prev, row]
              )
            }
          />
        )}

        <Divider />

        <Box
          sx={{
            px: 2,
            py: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid #e0e0e0",
            boxShadow: "5px -5px 10px 5px rgba(0, 0, 0, 0.25)"
          }}
        >
          <ByzMuiTypography isTitle fontSize={10}>
            {t("patient-diet-procedures.add-popup.your-selections")}
          </ByzMuiTypography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ByzMuiAddButton
              onClick={handleExportSelected}
              disabled={selectionModel.length === 0}
              popup
            />
            <ByzToggleIconButton
              iconOn="/assets/icons/generals/eye-open.svg"
              iconOff="/assets/icons/generals/eye-close.svg"
              altText="eye"
              onClick={() => setIsEyeOpen(!isEyeOpen)}
            />
            <ByzToggleIconButton
              iconOn="/assets/icons/generals/full-screen-off.svg"
              iconOff="/assets/icons/generals/full-screen-on.svg"
              altText="fullscreen"
              onClick={() => setIsFullscreen(!isFullscreen)}
            />
            <ByzToggleIconButton
              iconOn="/assets/icons/generals/arrow-up.svg"
              iconOff="/assets/icons/generals/arrow-down.svg"
              altText="arrow"
              onClick={() => setIsArrowUp(!isArrowUp)}
            />
          </Box>
        </Box>

        {!isArrowUp && (
          <Box sx={{ px: 2, pb: 2, flex: 1, overflowY: "auto" }}>
            <ByzMuiDataGrid
              rows={parentRows}
              columns={[
                parentActionsColumn,
                GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
                ...parentColumns,
              ]}
              detailPanelExpandedRowIds={expandedRowIds}
              onDetailPanelExpandedRowIdsChange={(ids) =>
                setExpandedRowIds(ids as string[])
              }
              pageSize={5}
              disableProcessMenu
              onColumnWidthChange={handleColumnResize}
              slots={{
                detailPanelExpandIcon: () => (
                  <img src="/assets/icons/vector/vector-down.svg"
                    alt="down" />
                ),
                detailPanelCollapseIcon: () => (
                  <img src="/assets/icons/vector/vector-right.svg"
                    alt="right" />
                ),
              }}
              sx={{ //IDOGAN;452471;05052025;DURUMLARA GORE ARKA PLAN RENKLERI DEGISTIRILDI
                "& .status-beklemede": {
                  bgcolor: "#FFCC004D",
                  "&:hover": { bgcolor: "#FFCC004D" },
                },
                "& .status-gonderildi": {
                  bgcolor: "#AEADA64D",
                  "&:hover": { bgcolor: "#AEADA64D" },
                },
              }}
              getDetailPanelContent={({ row }: any) => (
                <Box sx={{ p: 0 }}>
                  <ByzMuiDataGrid
                    rows={childMap[row.diyetZamani] ?? []}
                    columns={[
                      indentColumn,
                      childActionsColumn,
                      ...childColumns,
                    ]}
                    hideFooter
                    autoHeight
                    onColumnWidthChange={handleColumnResize}
                    sx={{
                      "& .MuiDataGrid-columnHeaders": { display: "none" },
                      "& .status-beklemede": {
                        bgcolor: "#FFCC004D",
                        "&:hover": { bgcolor: "#FFCC004D" },
                      },
                      "& .status-gonderildi": {
                        bgcolor: "#AEADA64D",
                        "&:hover": { bgcolor: "#AEADA64D" },
                      },
                    }}
                  />
                </Box>
              )}
              getDetailPanelHeight={({ row }: any) => {
                return "auto";
              }}
            />
          </Box>
        )}
      </Box>
      <OpenComponentDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        title={editRow?.adi ?? ""}
        showSaveButton
        onSaveClick={() => {
          console.log("Kaydetilen veri:", editRow);
          setEditDialogOpen(false);
        }}
      >
        <EditPage rowData={editRow} onClose={() => setEditDialogOpen(false)} />
      </OpenComponentDialog>
    </Box>
  );
};

export default DietPopup;
